import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { DOC_CATALOG, DOC_TYPE_LABELS, docStatus, STATUS_META } from "./docCatalog";
import { UploadCloud, FileText, Image as ImageIcon, Eye, Download, RefreshCw, Trash2, Share2, History, X, Loader2, Paperclip, ChevronDown, ChevronUp } from "lucide-react";

const VIS_LABELS = { privato: "Solo proprietario/agenzia", collaboratori: "Collaboratori agenzia", cliente: "Cliente autorizzato" };
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const MIMES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };
const API_BASE = process.env.REACT_APP_BACKEND_URL;
export { DOC_TYPE_LABELS };

export default function DocumentsManager({ propertyId, pendingDocs, setPendingDocs, onChanged }) {
    const [docs, setDocs] = useState([]);
    const [maxMb, setMaxMb] = useState(10);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(null); // doc_type in upload
    const [error, setError] = useState(null);
    const [openCats, setOpenCats] = useState({ certificazioni: true, catastale: true });
    const slotInputRef = useRef(null);
    const slotTypeRef = useRef("altro");

    const load = async () => {
        if (!propertyId) return;
        try { const r = await api.get(`/properties/${propertyId}/documents`); setDocs(r.data.items || []); setMaxMb(r.data.max_mb || 10); } catch (e) { console.error(e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [propertyId]);

    const upload = async (files, docType) => {
        setError(null);
        const list = Array.from(files || []).filter((f) => MIMES.includes(f.type));
        if (list.length === 0) { setError("Formato non supportato. Ammessi: PDF, JPG, PNG, WEBP."); return; }
        if (!propertyId) {
            setPendingDocs?.((p) => [...p, ...list.map((f) => ({ file: f, doc_type: docType }))]);
            return;
        }
        setUploading(docType);
        for (const f of list) {
            if (f.size > maxMb * 1024 * 1024) { setError(`"${f.name}" supera il limite di ${maxMb}MB.`); continue; }
            const fd = new FormData();
            fd.append("file", f);
            fd.append("doc_type", docType);
            try { await api.post(`/properties/${propertyId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
            catch (e) { setError(e?.response?.data?.detail || `Errore caricando ${f.name}`); }
        }
        setUploading(null);
        await load();
        onChanged?.();
    };

    const pickForSlot = (docType) => {
        slotTypeRef.current = docType;
        slotInputRef.current?.click();
    };

    const docsByType = {};
    for (const d of docs) (docsByType[d.doc_type] = docsByType[d.doc_type] || []).push(d);
    const pendingByType = {};
    for (const p of pendingDocs || []) (pendingByType[p.doc_type] = pendingByType[p.doc_type] || []).push(p);

    const catStats = (cat) => {
        let loaded = 0, stale = 0;
        for (const it of cat.items) {
            const list = docsByType[it.id] || [];
            if (list.length > 0) { loaded += 1; if (list.some((d) => docStatus(d) === "da_aggiornare")) stale += 1; }
        }
        return { loaded, stale, total: cat.items.length };
    };

    return (
        <div data-testid="documents-manager" className="space-y-3">
            <input ref={slotInputRef} data-testid="doc-file-input" type="file" multiple accept={ACCEPT} className="hidden"
                onChange={(e) => { upload(e.target.files, slotTypeRef.current); e.target.value = ""; }} />

            {/* Quick dropzone */}
            <div data-testid="doc-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files, "altro"); }}
                onClick={() => pickForSlot("altro")}
                className={`border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${dragOver ? "border-gold bg-gold/5" : "border-white/15 hover:border-gold/50"}`}>
                <UploadCloud size={18} className="mx-auto text-ink-400" />
                <p className="text-xs text-ink-100 mt-1.5">Trascina qui per un caricamento rapido (categoria "Altro"), oppure usa 📎 accanto al documento specifico</p>
                <p className="text-[10px] text-ink-500 mt-0.5">PDF, JPG, PNG, WEBP · max {maxMb}MB per file</p>
            </div>
            {error && <div data-testid="doc-error" className="text-red-400 text-xs">{error}</div>}

            {/* Categories */}
            {DOC_CATALOG.map((cat) => {
                const open = !!openCats[cat.id];
                const s = catStats(cat);
                return (
                    <div key={cat.id} className="border border-white/10 bg-ink-900/30">
                        <button type="button" data-testid={`doc-cat-${cat.id}`} onClick={() => setOpenCats((o) => ({ ...o, [cat.id]: !open }))}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
                            <span className="text-sm text-ink-50 font-serif">{cat.label}</span>
                            <span className="flex items-center gap-3 text-[11px] text-ink-400">
                                <span className={s.loaded > 0 ? "text-green-300" : ""}>{s.loaded}/{s.total} caricati</span>
                                {s.stale > 0 && <span className="text-amber-300">{s.stale} da aggiornare</span>}
                                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </span>
                        </button>
                        {open && (
                            <div className="border-t border-white/10">
                                {cat.items.map((it) => (
                                    <DocSlot key={it.id} item={it} docs={docsByType[it.id] || []} pending={pendingByType[it.id] || []}
                                        uploading={uploading === it.id} onPick={() => pickForSlot(it.id)} onChanged={() => { load(); onChanged?.(); }}
                                        setPendingDocs={setPendingDocs} createMode={!propertyId} />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Altri documenti */}
            {((docsByType["altro"] || []).length > 0 || (pendingByType["altro"] || []).length > 0) && (
                <div className="border border-white/10 bg-ink-900/30">
                    <div className="px-4 py-3 text-sm text-ink-50 font-serif border-b border-white/10">Altri documenti</div>
                    <DocSlot item={{ id: "altro", label: "Altro documento" }} docs={docsByType["altro"] || []} pending={pendingByType["altro"] || []}
                        uploading={uploading === "altro"} onPick={() => pickForSlot("altro")} onChanged={() => { load(); onChanged?.(); }}
                        setPendingDocs={setPendingDocs} createMode={!propertyId} />
                </div>
            )}
        </div>
    );
}

function DocSlot({ item, docs, pending, uploading, onPick, onChanged, setPendingDocs, createMode }) {
    const status = docs.length > 0 ? (docs.some((d) => docStatus(d) === "da_aggiornare") ? "da_aggiornare" : "caricato") : (pending.length > 0 ? "caricato" : "mancante");
    const meta = STATUS_META[status];
    return (
        <div data-testid={`doc-slot-${item.id}`} className="px-4 py-2.5 border-b border-white/5 last:border-b-0">
            <div className="flex items-center gap-3">
                <span title={meta.label} data-testid={`doc-status-${item.id}`} className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                <span className={`flex-1 text-xs ${docs.length > 0 || pending.length > 0 ? "text-ink-50" : "text-ink-400"}`}>{item.label}</span>
                <span className={`text-[10px] uppercase tracking-wider hidden sm:inline ${meta.text}`}>{meta.label}</span>
                <button type="button" data-testid={`doc-upload-${item.id}`} onClick={onPick}
                    className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 border border-white/15 text-ink-100 hover:border-gold hover:text-gold transition-colors">
                    {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />} Carica
                </button>
            </div>
            {pending.length > 0 && createMode && (
                <div className="mt-2 space-y-1">
                    <p className="text-[10px] text-gold">Verrà caricato alla pubblicazione:</p>
                    {pending.map((p, i) => (
                        <div key={p.file.name || i} className="flex items-center gap-2 text-[11px] text-ink-300 pl-5">
                            <FileText size={11} className="shrink-0" />
                            <span className="truncate flex-1">{p.file.name}</span>
                            <span className="text-ink-500">{fmtSize(p.file.size)}</span>
                            <button type="button" onClick={() => setPendingDocs((l) => l.filter((x) => x !== p))} className="text-ink-400 hover:text-red-300"><X size={11} /></button>
                        </div>
                    ))}
                </div>
            )}
            {docs.length > 0 && <div className="mt-2 space-y-2">{docs.map((d) => <DocRow key={d.id} doc={d} onChanged={onChanged} />)}</div>}
        </div>
    );
}

function DocRow({ doc, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const [shareMsg, setShareMsg] = useState(null);
    const replaceRef = useRef(null);
    const isImg = (doc.mime || "").startsWith("image/");
    const status = docStatus(doc);

    const updateMeta = async (patch) => { await api.put(`/documents/${doc.id}`, patch); onChanged(); };
    const remove = async () => {
        if (!window.confirm(`Eliminare "${doc.filename}" e le sue versioni?`)) return;
        await api.delete(`/documents/${doc.id}`); onChanged();
    };
    const replace = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        const fd = new FormData(); fd.append("file", f);
        try { await api.post(`/documents/${doc.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onChanged(); }
        catch (err) { alert(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); e.target.value = ""; }
    };
    const share = async () => {
        const r = await api.post(`/documents/${doc.id}/share`);
        const url = `${API_BASE}/api/documents/${doc.id}/download?token=${r.data.share_token}`;
        try { await navigator.clipboard.writeText(url); setShareMsg("Link copiato!"); }
        catch { setShareMsg(url); }
        setTimeout(() => setShareMsg(null), 4000);
    };
    const restore = async (idx) => { await api.post(`/documents/${doc.id}/restore/${idx}`); onChanged(); };

    return (
        <div data-testid={`doc-row-${doc.id}`} className="border border-white/10 bg-ink-950/60 p-2.5 ml-5">
            <div className="flex items-center gap-2.5 flex-wrap">
                {isImg ? <ImageIcon size={14} className="text-gold shrink-0" /> : <FileText size={14} className="text-gold shrink-0" />}
                <div className="flex-1 min-w-[140px]">
                    <div className="text-xs text-ink-50 truncate">{doc.filename}</div>
                    <div className="text-[10px] text-ink-500">
                        {fmtSize(doc.size)} · {fmtDate(doc.uploaded_at)}
                        {status === "da_aggiornare" && <span className="text-amber-300"> · da aggiornare</span>}
                        {doc.share_token && <span className="text-gold"> · condiviso</span>}
                    </div>
                </div>
                <select data-testid={`doc-visibility-${doc.id}`} value={doc.visibility} onChange={(e) => updateMeta({ visibility: e.target.value })}
                    className="bg-ink-950 border border-white/15 px-1.5 py-1 text-[10px] text-ink-50 outline-none">
                    {Object.entries(VIS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="flex items-center gap-1">
                    <IconBtn testid={`doc-preview-${doc.id}`} title="Anteprima" onClick={() => window.open(`${API_BASE}/api/documents/${doc.id}/download?preview=true`, "_blank")}><Eye size={12} /></IconBtn>
                    <IconBtn testid={`doc-download-${doc.id}`} title="Download" onClick={() => window.open(`${API_BASE}/api/documents/${doc.id}/download`, "_blank")}><Download size={12} /></IconBtn>
                    <IconBtn testid={`doc-replace-${doc.id}`} title="Sostituisci" onClick={() => replaceRef.current?.click()}>{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}</IconBtn>
                    <IconBtn testid={`doc-share-${doc.id}`} title="Condividi link" onClick={share}><Share2 size={12} /></IconBtn>
                    {(doc.versions || []).length > 0 && <IconBtn testid={`doc-versions-${doc.id}`} title="Storico versioni" onClick={() => setShowVersions(!showVersions)}><History size={12} /></IconBtn>}
                    <IconBtn testid={`doc-delete-${doc.id}`} title="Elimina" danger onClick={remove}><Trash2 size={12} /></IconBtn>
                </div>
                <input ref={replaceRef} type="file" accept={ACCEPT} className="hidden" onChange={replace} />
            </div>
            {shareMsg && <div className="text-[11px] text-gold mt-2 break-all">{shareMsg}</div>}
            {showVersions && (
                <div className="mt-2 border-t border-white/10 pt-2 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Versioni precedenti</div>
                    {(doc.versions || []).map((v, i) => (
                        <div key={`${v.filename || "v"}-${v.uploaded_at || i}`} className="flex items-center gap-3 text-[11px] text-ink-300">
                            <span className="flex-1 truncate">{v.filename}</span>
                            <span className="text-ink-500">{fmtSize(v.size)} · {fmtDate(v.uploaded_at)}</span>
                            <button type="button" data-testid={`doc-restore-${doc.id}-${i}`} onClick={() => restore(i)} className="text-gold hover:underline">Ripristina</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function IconBtn({ children, onClick, title, danger, testid }) {
    return (
        <button type="button" data-testid={testid} title={title} onClick={onClick}
            className={`p-1.5 border border-white/15 transition-colors ${danger ? "text-ink-400 hover:text-red-300 hover:border-red-700/40" : "text-ink-400 hover:text-gold hover:border-gold/40"}`}>
            {children}
        </button>
    );
}
